import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowRightLeft,
  Boxes,
  History,
  Package2,
  PencilLine,
  Search,
  Warehouse,
  X,
} from "lucide-react";

import { Button } from "../utils/components/ui/button";
import PaginationComponent from "../components/PaginationComponent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../utils/components/ui/dialog";
import { Input } from "../utils/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../utils/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../utils/components/ui/tabs";
import { useGetAllBranches } from "../service/branchService";
import {
  useGetAllProductData,
  useSetProductRack,
} from "../service/productService";
import {
  useCreateStockTransfer,
  useGetStockTransferHistory,
  type StockTransferHistoryItem as ApiStockTransferHistoryItem,
} from "../service/stockTransferService";
import { Branch } from "../types/branchType";
import { ProductT } from "../types/productTypes";
import { toast } from "react-toastify";
import {
  calculateBaseQuantity,
  getDefaultUnitOption,
  getUnitOption,
  normalizeUnitOptions,
  type UnitOption,
} from "../utils/unitConversion";
import { stockTypes } from "../utils/constants/constants";

const INVENTORY_PAGE_SIZE = 10;

type InventoryRecord = {
  id: string;
  productId: number;
  branchId: number;
  productName: string;
  location: string;
  branchType: Branch["type"];
  availableQty: number;
  itemType: string;
  unit: string;
  displayQuantity: string;
  unitOptions: UnitOption[];
  status: "In Stock" | "Low Stock" | "Out of Stock";
  rackName: string;
  rackLabel: string;
  shelfLabel: string;
  transferHistory: TransferHistoryItem[];
};

type TransferHistoryItem = {
  id: string;
  destinationName: string;
  qty: number;
  date: string;
  time: string;
};

type UpdateInventoryForm = {
  availableQty: string;
  rackLabel: string;
};

type TransferForm = {
  toBranchId: string;
  quantity: string;
  unit: string;
  conversionToBaseUnit: number;
};

const formatStatus = (
  stockStatus?: ProductT["stockStatus"],
): InventoryRecord["status"] => {
  if (stockStatus === "LOW_STOCK") return "Low Stock";
  if (stockStatus === "OUT_STOCK") return "Out of Stock";
  return "In Stock";
};

const mapProductToInventoryRecord = (
  product: ProductT,
  branch: Branch | undefined,
): InventoryRecord => {
  const rackLabels = product.rackLabels?.filter(Boolean) || [];

  return {
    id: `${branch?.branchId || "branch"}-${product.id}`,
    productId: product.id,
    branchId: branch?.branchId || 0,
    productName: product.productName,
    location: branch?.branchName || "Selected branch",
    branchType: branch?.type || "SHOP",
    availableQty: Number(product.availableQuantity ?? product.qty ?? 0),
    itemType:
      product.subCategory?.categoryName ||
      product.mainCategory?.categoryName ||
      "Product",
    unit: product.unit || "-",
    displayQuantity:
      product.displayQuantity ||
      `${Number(product.availableQuantity ?? product.qty ?? 0)} ${product.unit || ""}`.trim(),
    unitOptions: normalizeUnitOptions(product.unitOptions, product.unit),
    status: formatStatus(product.stockStatus),
    rackName: product.rackName || "",
    rackLabel: product.rackLabel || rackLabels[0] || "",
    shelfLabel: product.shelfLabel || rackLabels[1] || "",
    transferHistory: [],
  };
};

function Inventory() {
  const queryClient = useQueryClient();
  const [activeBranchId, setActiveBranchId] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [editingRecord, setEditingRecord] = useState<InventoryRecord | null>(
    null,
  );
  const [transferRecord, setTransferRecord] = useState<InventoryRecord | null>(
    null,
  );
  const [historyRecord, setHistoryRecord] = useState<InventoryRecord | null>(
    null,
  );
  const [updateForm, setUpdateForm] = useState<UpdateInventoryForm>({
    availableQty: "",
    rackLabel: "",
    shelfLabel: "",
  });
  const [transferForm, setTransferForm] = useState<TransferForm>({
    toBranchId: "",
    quantity: "",
    unit: "",
    conversionToBaseUnit: 1,
  });

  const { data: branchData, isLoading: isBranchesLoading } = useGetAllBranches({
    params: {
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "branchName",
      sortDir: "asc",
    },
  });

  const branches = useMemo(() => branchData?.content || [], [branchData]);
  const activeBranch = useMemo(
    () => branches.find((branch) => String(branch.branchId) === activeBranchId),
    [activeBranchId, branches],
  );

  useEffect(() => {
    if (activeBranchId || branches.length === 0) return;

    const storedBranchId = localStorage.getItem("branchId");
    const defaultBranch =
      branches.find((branch) => String(branch.branchId) === storedBranchId) ||
      branches[0];

    setActiveBranchId(String(defaultBranch.branchId));
  }, [activeBranchId, branches]);

  const { data: productData, isLoading: isProductsLoading } =
    useGetAllProductData({
      branchId: activeBranch?.branchId || 0,
      pageNo: currentPage,
      pageSize: INVENTORY_PAGE_SIZE,
      productName: searchTerm || undefined,
      stockStatus: statusFilter === "all" ? undefined : statusFilter,
    });

  const inventoryRecords = useMemo(() => {
    return (productData?.productResponseList || []).map((product) =>
      mapProductToInventoryRecord(product, activeBranch),
    );
  }, [activeBranch, productData]);

  const { mutate: createStockTransfer, isPending: isTransferPending } =
    useCreateStockTransfer();
  const { mutate: setProductRack, isPending: isRackUpdatePending } =
    useSetProductRack();

  const totalPages = productData?.totalPages || 0;
  const totalElementsBackend = productData?.totalElements || 0;

  const currentRecords = inventoryRecords;

  const locationOptions = useMemo(() => {
    return Array.from(new Set(currentRecords.map((item) => item.location)));
  }, [currentRecords]);

  const filteredRecords = useMemo(() => {
    return currentRecords.filter((item) => {
      const matchesSearch =
        item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.itemType.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLocation =
        locationFilter === "all" || item.location === locationFilter;
      const matchesStatus =
        statusFilter === "all" ||
        item.status === stockTypes.find((opt) => opt.id === statusFilter)?.name;

      return matchesSearch && matchesLocation && matchesStatus;
    });
  }, [currentRecords, locationFilter, statusFilter, searchTerm]);

  const storeCount = branches.filter((branch) => branch.type === "SHOP").length;
  const warehouseCount = branches.filter(
    (branch) => branch.type === "WAREHOUSE",
  ).length;
  const totalUnits = filteredRecords.reduce(
    (sum, item) => sum + item.availableQty,
    0,
  );

  const destinationBranches = useMemo(() => {
    return branches.filter(
      (branch) => String(branch.branchId) !== activeBranchId,
    );
  }, [activeBranchId, branches]);

  const handleOpenEdit = (record: InventoryRecord) => {
    setEditingRecord(record);
    setUpdateForm({
      availableQty: String(record.availableQty),
      rackLabel: record.rackLabel,
    });
  };

  const handleOpenTransfer = (record: InventoryRecord) => {
    const option = getDefaultUnitOption(record.unitOptions, record.unit);
    setTransferRecord(record);
    setTransferForm({
      toBranchId: "",
      quantity: "",
      unit: option?.unit || record.unit,
      conversionToBaseUnit: option?.conversionToBaseUnit || 1,
    });
  };

  const handleUpdateStock = () => {
    if (!editingRecord) return;

    setProductRack(
      {
        branchId: editingRecord.branchId,
        productId: editingRecord.productId,
        rackName: editingRecord.rackName,
        rackLabel: updateForm.rackLabel.trim(),
        shelfLabel: editingRecord.shelfLabel,
      },
      {
        onSuccess: () => {
          toast.success("Rack and shelf updated successfully.");
          queryClient.invalidateQueries({ queryKey: ["useGetAllProduct"] });
          setEditingRecord(null);
        },
        onError: (error: Error) => {
          toast.error(`Failed to update rack and shelf: ${error.message}`);
        },
      },
    );
  };

  const handleTransferStock = () => {
    if (!transferRecord || !transferForm.toBranchId) return;

    const transferQty = Number(transferForm.quantity || 0);
    if (
      transferQty <= 0 ||
      calculateBaseQuantity(transferQty, transferForm.conversionToBaseUnit) >
        transferRecord.availableQty
    )
      return;

    createStockTransfer(
      {
        productId: transferRecord.productId,
        quantity: transferQty,
        unit: transferForm.unit,
        conversionToBaseUnit: transferForm.conversionToBaseUnit,
        baseQuantity: calculateBaseQuantity(
          transferQty,
          transferForm.conversionToBaseUnit,
        ),
        fromBranchId: transferRecord.branchId,
        toBranchId: Number(transferForm.toBranchId),
        transferDate: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          toast.success("Stock transferred successfully.");
          queryClient.invalidateQueries({ queryKey: ["useGetAllProduct"] });
          queryClient.invalidateQueries({
            queryKey: ["useGetStockTransferHistory"],
          });
          setTransferRecord(null);
        },
        onError: (error: Error) => {
          toast.error(`Failed to transfer stock: ${error.message}`);
        },
      },
    );
  };

  return (
    <div className="min-h-full py-1">
      <div className="space-y-6">
        <section className="rounded-[26px] border border-[#E8EEF9] bg-white px-6 py-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <h1 className="text-[34px] font-semibold tracking-[-0.03em] text-[#1A5EF9]">
                Inventory Module
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-[#7D8998]">
                Monitor store and warehouse stock, maintain storage details, and
                move products across locations from one consistent workspace.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatCard
                icon={<Package2 className="size-5" />}
                title="Store Items"
                value={storeCount}
                subtitle="Visible store records"
              />
              <StatCard
                icon={<Warehouse className="size-5" />}
                title="Warehouse Items"
                value={warehouseCount}
                subtitle="Active warehouse records"
              />
              <StatCard
                icon={<Boxes className="size-5" />}
                title="Units in View"
                value={totalUnits}
                subtitle="Total quantity after filters"
              />
            </div>
          </div>
        </section>

        <Tabs
          value={activeBranchId}
          onValueChange={(value) => {
            setActiveBranchId(value);
            setCurrentPage(0);
            setLocationFilter("all");
            setStatusFilter("all");
            setTransferRecord(null);
            setHistoryRecord(null);
          }}
          className="space-y-6"
        >
          <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <TabsList className="flex h-auto w-full max-w-full justify-start gap-2 overflow-x-auto rounded-[18px] border border-[#E4ECFA] bg-[#F5F8FD] p-1 xl:max-w-[560px]">
                {branches.map((branch) => (
                  <TabsTrigger
                    key={branch.branchId}
                    value={String(branch.branchId)}
                    className="whitespace-nowrap rounded-[14px] px-4 py-3 text-sm font-medium text-[#62738E] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white"
                  >
                    {branch.branchName}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="flex flex-col gap-4 lg:flex-row">
                <div className="relative min-w-[280px] flex-1">
                  <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#91A0B8]" />
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder={`Search ${
                      activeBranch?.branchName || "branch"
                    } stock`}
                    className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  />
                </div>

                <Select
                  value={locationFilter}
                  onValueChange={setLocationFilter}
                >
                  <SelectTrigger className="h-12 min-w-[200px] rounded-[14px] border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#5D6E8A]">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locationOptions.map((location) => (
                      <SelectItem key={location} value={location}>
                        {location}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={statusFilter}
                  onValueChange={setStatusFilter}
                >
                  <SelectTrigger className="h-12 min-w-[200px] rounded-[14px] border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#5D6E8A]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {stockTypes.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {activeBranchId ? (
            <TabsContent value={activeBranchId} className="space-y-6">
              <InventoryTable
                title={`${activeBranch?.branchName || "Branch"} Inventory`}
                description="Search, review, update, or transfer inventory with cleaner actions and storage visibility."
                records={filteredRecords}
                actionLabel="Products Found"
                onEdit={handleOpenEdit}
                onTransfer={handleOpenTransfer}
                onViewHistory={setHistoryRecord}
                showTransferAction
                isLoading={isBranchesLoading || isProductsLoading}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </TabsContent>
          ) : null}
          {!activeBranchId && !isBranchesLoading ? (
            <div className="rounded-[26px] border border-dashed border-[#D6E2F6] bg-white px-6 py-14 text-center text-sm text-[#7D8998]">
              No active branches are available for inventory.
            </div>
          ) : null}
        </Tabs>
      </div>

      <UpdateStockDialog
        open={Boolean(editingRecord)}
        record={editingRecord}
        form={updateForm}
        onChange={setUpdateForm}
        onClose={() => setEditingRecord(null)}
        onSubmit={handleUpdateStock}
        isSubmitting={isRackUpdatePending}
      />

      <TransferStockDialog
        open={Boolean(transferRecord)}
        record={transferRecord}
        form={transferForm}
        branchOptions={destinationBranches}
        onChange={setTransferForm}
        onClose={() => setTransferRecord(null)}
        onSubmit={handleTransferStock}
        isSubmitting={isTransferPending}
      />

      <TransferHistoryDialog
        open={Boolean(historyRecord)}
        record={historyRecord}
        onClose={() => setHistoryRecord(null)}
      />
    </div>
  );
}

function StatCard({
  icon,
  title,
  value,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  value: number;
  subtitle: string;
}) {
  return (
    <div className="rounded-[20px] border border-[#E8EEF9] bg-[#F8FBFF] p-4 shadow-[0_10px_28px_rgba(26,94,249,0.06)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-[#7B8797]">{title}</p>
          <p className="mt-2 text-[28px] font-semibold tracking-[-0.02em] text-[#1A2B49]">
            {value}
          </p>
          <p className="mt-1 text-sm text-[#8D98A7]">{subtitle}</p>
        </div>
        <div className="rounded-[16px] bg-[#EEF4FF] p-3 text-[#1A5EF9]">
          {icon}
        </div>
      </div>
    </div>
  );
}

function InventoryTable({
  title,
  description,
  records,
  actionLabel,
  onEdit,
  onTransfer,
  onViewHistory,
  showTransferAction,
  isLoading,
  currentPage,
  totalPages,
  onPageChange,
}: {
  title: string;
  description: string;
  records: InventoryRecord[];
  actionLabel: string;
  onEdit: (record: InventoryRecord) => void;
  onTransfer?: (record: InventoryRecord) => void;
  onViewHistory: (record: InventoryRecord) => void;
  showTransferAction: boolean;
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
      <div className="flex flex-col gap-4 border-b border-[#E8EEF9] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#21314A]">{title}</h2>
          <p className="mt-1 text-sm text-[#7D8998]">{description}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="rounded-[14px] bg-[#F5F8FD] px-4 py-3 text-sm text-[#7D8998]">
            <span className="font-semibold text-[#253650]">{records.length}</span>{" "}
            {actionLabel}
          </div>
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-[#F5F8FD] text-left text-sm font-semibold text-[#31425E]">
              <th className="px-6 py-4">Product</th>
              <th className="px-6 py-4">Location</th>
              <th className="px-6 py-4 text-right">Available Qty</th>
              <th className="px-6 py-4">Item Type</th>
              <th className="px-6 py-4">Unit</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-16 text-center text-sm text-[#8D98A7]"
                >
                  Loading inventory records...
                </td>
              </tr>
            ) : records.length > 0 ? (
              records.map((record) => (
                <tr
                  key={record.id}
                  className="border-t border-[#EEF2FA] text-sm text-[#43536A] transition-colors hover:bg-[#FAFCFF]"
                >
                  <td className="px-6 py-4 font-semibold text-[#24344F]">
                    {record.productName}
                  </td>
                  <td className="px-6 py-4 text-[#5E708E]">
                    {record.location}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-[#24344F]">
                    {record.displayQuantity}
                  </td>
                  <td className="px-6 py-4 text-[#5E708E]">
                    {record.itemType}
                  </td>
                  <td className="px-6 py-4 text-[#5E708E]">{record.unit}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        record.status === "In Stock"
                          ? "bg-[#EAF2FF] text-[#1A5EF9]"
                          : record.status === "Low Stock"
                            ? "bg-[#FFF1E6] text-[#C76A12]"
                            : "bg-[#FEE2E2] text-[#EF4444]"
                      }`}
                    >
                      {record.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => onEdit(record)}
                        title="Edit stock"
                        aria-label="Edit stock"
                        className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                      >
                        <PencilLine className="size-4" />
                        <span className="sr-only">Edit stock</span>
                      </button>

                      {showTransferAction && onTransfer ? (
                        <button
                          type="button"
                          onClick={() => onTransfer(record)}
                          title="Transfer stock"
                          aria-label="Transfer stock"
                          className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                        >
                          <ArrowRightLeft className="size-4" />
                          <span className="sr-only">Transfer stock</span>
                        </button>
                      ) : null}

                      <button
                        type="button"
                        onClick={() => onViewHistory(record)}
                        title="View stock history"
                        aria-label="View stock history"
                        className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                      >
                        <History className="size-4" />
                        <span className="sr-only">View history</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-16 text-center text-sm text-[#8D98A7]"
                >
                  No inventory records match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UpdateStockDialog({
  open,
  record,
  form,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  record: InventoryRecord | null;
  form: UpdateInventoryForm;
  onChange: (form: UpdateInventoryForm) => void;
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="w-full max-w-[720px] overflow-hidden rounded-[28px] border border-[#DCE7FB] bg-white p-0 shadow-[0_28px_70px_rgba(26,94,249,0.18)]"
      >
        <DialogHeader className="border-b border-[#E8EEF9] bg-[linear-gradient(135deg,#F7FAFF_0%,#EAF2FF_100%)] px-6 py-6 text-left sm:px-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="text-[30px] font-semibold tracking-[-0.03em] text-[#123C8E]">
                {record?.location || "Store"}
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-6 text-[#62738E]">
                Update rack and shelf details for this product.
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-[#D7E2F5] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
            >
              <X className="size-4" />
            </button>
          </div>
        </DialogHeader>

        <div className="space-y-5 px-6 py-6 sm:px-8">
          <Field label="Product Name">
            <Input
              value={record?.productName || ""}
              readOnly
              className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Available Quantity">
              <Input
                value={form.availableQty}
                readOnly
                className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
              />
            </Field>
            <Field label="Item Type">
              <Input
                value={record?.itemType || ""}
                readOnly
                className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
              />
            </Field>
            <Field label="Unit">
              <Input
                value={record?.unit || ""}
                readOnly
                className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
              />
            </Field>
          </div>

          <div className="border-t border-[#E8EEF9] pt-5">
            <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-[#6C7D96]">
              Storage Information
            </h3>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Field label="Rack Label">
                <Input
                  value={form.rackLabel}
                  onChange={(event) =>
                    onChange({ ...form, rackLabel: event.target.value })
                  }
                  className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
                />
              </Field>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-[#E8EEF9] px-6 py-5 sm:px-8">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="h-11 rounded-[12px] border-[#D6E2F6] px-6 text-[#46607C] hover:bg-[#F4F8FF]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
          >
            {isSubmitting ? "Updating..." : "Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferStockDialog({
  open,
  record,
  form,
  branchOptions,
  onChange,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  record: InventoryRecord | null;
  form: TransferForm;
  branchOptions: Branch[];
  onChange: (form: TransferForm) => void;
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}) {
  const requestedQty = Number(form.quantity || 0);
  const requestedBaseQty = calculateBaseQuantity(
    requestedQty,
    form.conversionToBaseUnit,
  );
  const isDisabled =
    !record ||
    !form.toBranchId ||
    requestedQty <= 0 ||
    requestedBaseQty > record.availableQty ||
    isSubmitting;

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="w-full max-w-[640px] overflow-hidden rounded-[28px] border border-[#DCE7FB] bg-white p-0 shadow-[0_28px_70px_rgba(26,94,249,0.18)]"
      >
        <DialogHeader className="border-b border-[#E8EEF9] bg-[linear-gradient(135deg,#F7FAFF_0%,#EAF2FF_100%)] px-6 py-6 text-left sm:px-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="text-[30px] font-semibold tracking-[-0.03em] text-[#123C8E]">
                Transfer Stock
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-6 text-[#62738E]">
                Move selected stock to another branch while preserving the
                transfer log.
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-[#D7E2F5] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
            >
              <X className="size-4" />
            </button>
          </div>
        </DialogHeader>

        <div className="space-y-5 px-6 py-6 sm:px-8">
          <Field label="Product Name">
            <Input
              value={record?.productName || ""}
              readOnly
              className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Available Quantity">
              <Input
                value={String(record?.availableQty || "")}
                readOnly
                className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
              />
            </Field>
            <Field label="Item Type">
              <Input
                value={record?.itemType || ""}
                readOnly
                className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
              />
            </Field>
            <Field label="Unit">
              <Input
                value={record?.unit || ""}
                readOnly
                className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
              />
            </Field>
          </div>

          <div className="border-t border-[#E8EEF9] pt-5">
            <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-[#6C7D96]">
              Transfer Details
            </h3>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Field label="To Branch">
                <Select
                  value={form.toBranchId}
                  onValueChange={(value) =>
                    onChange({ ...form, toBranchId: value })
                  }
                >
                  <SelectTrigger className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#5D6E8A]">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branchOptions.map((branch) => (
                      <SelectItem
                        key={branch.branchId}
                        value={String(branch.branchId)}
                      >
                        {branch.branchName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Quantity">
                <Input
                  value={form.quantity}
                  onChange={(event) =>
                    onChange({ ...form, quantity: event.target.value })
                  }
                  placeholder="Enter quantity"
                  className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
                />
              </Field>
              <Field label="Unit">
                <Select
                  value={form.unit}
                  onValueChange={(value) =>
                    onChange({
                      ...form,
                      unit: value,
                      conversionToBaseUnit:
                        getUnitOption(record?.unitOptions, value, record?.unit)
                          ?.conversionToBaseUnit || 1,
                    })
                  }
                >
                  <SelectTrigger className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {normalizeUnitOptions(
                      record?.unitOptions,
                      record?.unit,
                    ).map((option) => (
                      <SelectItem key={option.unit} value={option.unit}>
                        {option.unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          {requestedBaseQty > (record?.availableQty || 0) ? (
            <p className="text-sm font-medium text-[#D14343]">
              Transfer quantity cannot exceed the available quantity.
            </p>
          ) : null}
        </div>

        <DialogFooter className="border-t border-[#E8EEF9] px-6 py-5 sm:px-8">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="h-11 rounded-[12px] border-[#D6E2F6] px-6 text-[#46607C] hover:bg-[#F4F8FF]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={isDisabled}
            className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferHistoryDialog({
  open,
  record,
  onClose,
}: {
  open: boolean;
  record: InventoryRecord | null;
  onClose: () => void;
}) {
  const { data: historyData, isLoading } = useGetStockTransferHistory({
    productId: record?.productId,
    fromBranchId: record?.branchId,
    pageNo: 0,
    pageSize: 10,
  });

  const apiHistoryItems = useMemo(() => {
    const response = historyData as
      | {
          content?: ApiStockTransferHistoryItem[];
          stockTransferResponseList?: ApiStockTransferHistoryItem[];
          transferHistory?: ApiStockTransferHistoryItem[];
        }
      | undefined;

    return (
      response?.content ||
      response?.stockTransferResponseList ||
      response?.transferHistory ||
      []
    );
  }, [historyData]);

  const historyItems = useMemo<TransferHistoryItem[]>(() => {
    return apiHistoryItems.map((item, index) => {
      const transferDate = item.transferDate || item.createdAt || "";
      const dateValue = transferDate ? new Date(transferDate) : null;
      const hasValidDate = dateValue && !Number.isNaN(dateValue.getTime());

      return {
        id: String(
          item.id || `${item.productId || record?.productId}-${index}`,
        ),
        destinationName:
          item.toBranchName ||
          item.warehouseName ||
          `Branch ${item.toBranchId}`,
        qty: Number(item.quantity || item.qty || 0),
        date: hasValidDate
          ? new Intl.DateTimeFormat("en-GB").format(dateValue)
          : "-",
        time: hasValidDate
          ? new Intl.DateTimeFormat("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            }).format(dateValue)
          : "-",
      };
    });
  }, [apiHistoryItems, record?.productId]);

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="w-full max-w-[720px] overflow-hidden rounded-[28px] border border-[#DCE7FB] bg-white p-0 shadow-[0_28px_70px_rgba(26,94,249,0.18)]"
      >
        <DialogHeader className="border-b border-[#E8EEF9] bg-[linear-gradient(135deg,#F7FAFF_0%,#EAF2FF_100%)] px-6 py-6 text-left sm:px-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="text-[30px] font-semibold tracking-[-0.03em] text-[#123C8E]">
                Transfer History
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-6 text-[#62738E]">
                Review transport history for the selected product and source
                location.
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-[#D7E2F5] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
            >
              <X className="size-4" />
            </button>
          </div>
        </DialogHeader>

        <div className="space-y-5 px-6 py-6 sm:px-8">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Product">
              <Input
                value={record?.productName || ""}
                readOnly
                className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
              />
            </Field>
            <Field label="From Store">
              <Input
                value={record?.location || ""}
                readOnly
                className="h-11 rounded-xl border-[#E5ECF8] bg-[#F5F8FD]"
              />
            </Field>
          </div>

          <div className="space-y-3">
            {isLoading ? (
              <div className="rounded-[18px] border border-dashed border-[#D6E2F6] bg-[#FBFDFF] px-4 py-8 text-center text-sm text-[#7D8998]">
                Loading transfer history...
              </div>
            ) : historyItems.length > 0 ? (
              historyItems.map((historyItem) => (
                <div
                  key={historyItem.id}
                  className="rounded-[18px] border border-[#D6E2F6] bg-[#FBFDFF] px-4 py-4"
                >
                  <p className="text-sm font-semibold text-[#24344F]">
                    {historyItem.destinationName}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-[#5D6E8A]">
                    <span>Qty: {historyItem.qty}</span>
                    <span>Date: {historyItem.date}</span>
                    <span>Time: {historyItem.time}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-[18px] border border-dashed border-[#D6E2F6] bg-[#FBFDFF] px-4 py-8 text-center text-sm text-[#7D8998]">
                No transfer history is available for this item yet.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-2">
      <span className="text-sm font-medium text-[#5D6E8A]">{label}</span>
      {children}
    </label>
  );
}

export default Inventory;
